import type {
    AgentInteractionState,
    OrchestratorAgent,
    OrchestratorState,
    SimpleAgentConfig
} from '@demo-portal/shared';
import React, { useEffect, useState } from 'react';

interface OrchestrationTimelineProps {
  orchestrator: OrchestratorAgent;
  agents: SimpleAgentConfig;
  orchestratorState: OrchestratorState;
  agentInteractions: Map<string, AgentInteractionState>;
  primaryColor?: string;
}

interface TimelineEvent {
  id: string;
  timestamp: number;
  type: 'orchestrator' | 'agent' | 'system';
  agent: string;
  action: string;
  description: string;
  status: 'completed' | 'active' | 'pending';
  duration?: number;
}

const OrchestrationTimeline: React.FC<OrchestrationTimelineProps> = ({
  orchestrator,
  agents,
  orchestratorState,
  agentInteractions,
  primaryColor = '#667eea',
}) => {
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update timeline based on orchestration state
  useEffect(() => {
    const events: TimelineEvent[] = [];
    const baseTime = Date.now() - 30000; // Start timeline 30 seconds ago

    // Add initialization event
    events.push({
      id: 'init',
      timestamp: baseTime,
      type: 'system',
      agent: 'system',
      action: 'initialize',
      description: 'Orchestration system initialized',
      status: 'completed',
      duration: 2000,
    });

    // Add discovery events
    if (orchestratorState.discoveredAgents.length > 0) {
      events.push({
        id: 'discovery',
        timestamp: baseTime + 2000,
        type: 'orchestrator',
        agent: 'orchestrator',
        action: 'discover',
        description: `Discovered ${orchestratorState.discoveredAgents.length} specialized agents`,
        status: 'completed',
        duration: 3000,
      });
    }

    // Add current step if active
    if (orchestratorState.currentStep) {
      const step = orchestratorState.currentStep;
      events.push({
        id: step.stepId,
        timestamp: baseTime + 8000,
        type: step.agent === 'orchestrator' ? 'orchestrator' : 'agent',
        agent: step.agent,
        action: step.action,
        description: step.description,
        status: 'active',
        duration: step.duration,
      });
    }

    // Add completed agent interactions
    Array.from(agentInteractions.entries()).forEach(([agentId, interaction], index) => {
      if (interaction.status === 'complete') {
        events.push({
          id: `agent-${agentId}`,
          timestamp: baseTime + 5000 + (index * 2000),
          type: 'agent',
          agent: agentId,
          action: 'process',
          description: `${agents.find(a => a.id === agentId)?.name || agentId} completed analysis`,
          status: 'completed',
          duration: 3000,
        });
      }
    });

    // Add queued steps
    orchestratorState.processingQueue.forEach((step, index) => {
      events.push({
        id: `queued-${step.stepId}`,
        timestamp: baseTime + 15000 + (index * 1000),
        type: step.agent === 'orchestrator' ? 'orchestrator' : 'agent',
        agent: step.agent,
        action: step.action,
        description: step.description,
        status: 'pending',
        duration: step.duration,
      });
    });

    // Sort events by timestamp
    events.sort((a, b) => a.timestamp - b.timestamp);
    setTimelineEvents(events);
  }, [orchestrator, agents, orchestratorState, agentInteractions]);

  // Update current time for progress calculations
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const getEventIcon = (type: TimelineEvent['type'], action: string) => {
    switch (type) {
      case 'orchestrator':
        return action === 'analyze' ? '🧠' : action === 'synthesize' ? '🔄' : '🎯';
      case 'agent':
        return '🤖';
      case 'system':
        return '⚙️';
      default:
        return '📋';
    }
  };

  const getEventColor = (type: TimelineEvent['type'], status: TimelineEvent['status']) => {
    if (status === 'completed') return '#27ae60';
    if (status === 'active') return primaryColor;
    if (status === 'pending') return '#95a5a6';
    
    switch (type) {
      case 'orchestrator':
        return '#667eea';
      case 'agent':
        return '#3498db';
      case 'system':
        return '#e67e22';
      default:
        return '#95a5a6';
    }
  };

  const getAgentInfo = (agentId: string) => {
    if (agentId === 'orchestrator') {
      return { name: orchestrator.name, avatar: orchestrator.avatar };
    }
    if (agentId === 'system') {
      return { name: 'System', avatar: '⚙️' };
    }
    const agent = agents.find(a => a.id === agentId);
    return { 
      name: agent?.name || 'Unknown Agent', 
      avatar: agent?.avatar || '🤖' 
    };
  };

  const calculateProgress = (event: TimelineEvent) => {
    if (event.status !== 'active' || !event.duration) return 0;
    
    const elapsed = currentTime - event.timestamp;
    const progress = Math.min(100, Math.max(0, (elapsed / event.duration) * 100));
    return progress;
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      height: '400px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        fontSize: '1.3em',
        fontWeight: 'bold',
        marginBottom: '20px',
        color: '#2c3e50',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
      }}>
        <span style={{ fontSize: '1.2em' }}>⏱️</span>
        Orchestration Timeline
      </div>

      {/* Timeline container */}
      <div style={{
        height: '320px',
        overflowY: 'auto',
        position: 'relative',
        paddingLeft: '20px',
      }}>
        {/* Timeline line */}
        <div style={{
          position: 'absolute',
          left: '35px',
          top: '0',
          bottom: '0',
          width: '3px',
          background: `linear-gradient(to bottom, ${primaryColor}, ${primaryColor}66)`,
          borderRadius: '2px',
        }} />

        {/* Timeline events */}
        {timelineEvents.map((event, index) => {
          const agentInfo = getAgentInfo(event.agent);
          const eventColor = getEventColor(event.type, event.status);
          const progress = calculateProgress(event);
          const isActive = event.status === 'active';

          return (
            <div
              key={event.id}
              style={{
                position: 'relative',
                marginBottom: '20px',
                paddingLeft: '40px',
                opacity: event.status === 'pending' ? 0.6 : 1,
                transition: 'all 0.3s ease',
              }}
            >
              {/* Timeline dot */}
              <div style={{
                position: 'absolute',
                left: '-8px',
                top: '8px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: eventColor,
                border: '3px solid white',
                boxShadow: isActive 
                  ? `0 0 15px ${eventColor}66` 
                  : '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7em',
                animation: isActive ? 'pulse 2s infinite' : 'none',
                zIndex: 2,
              }}>
                {event.status === 'completed' ? '✓' : 
                 event.status === 'active' ? '⚡' : 
                 '⏳'}
              </div>

              {/* Event content */}
              <div style={{
                background: isActive 
                  ? `linear-gradient(135deg, ${eventColor}15, ${eventColor}05)`
                  : 'rgba(248, 249, 250, 0.8)',
                border: `1px solid ${isActive ? eventColor : '#e9ecef'}`,
                borderRadius: '12px',
                padding: '12px 16px',
                marginLeft: '10px',
                position: 'relative',
                transition: 'all 0.3s ease',
              }}>
                {/* Event header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '6px',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: 'bold',
                    color: '#2c3e50',
                  }}>
                    <span style={{ fontSize: '1.1em' }}>
                      {getEventIcon(event.type, event.action)}
                    </span>
                    <span>{agentInfo.name}</span>
                  </div>
                  
                  <div style={{
                    fontSize: '0.75em',
                    color: '#7f8c8d',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    {event.status === 'active' && event.duration && (
                      <div style={{
                        background: '#ecf0f1',
                        borderRadius: '8px',
                        padding: '2px 6px',
                        fontSize: '0.8em',
                        fontWeight: 'bold',
                        color: eventColor,
                      }}>
                        {Math.round(progress)}%
                      </div>
                    )}
                    <span>
                      {new Date(event.timestamp).toLocaleTimeString([], { 
                        hour: '2-digit', 
                        minute: '2-digit', 
                        second: '2-digit' 
                      })}
                    </span>
                  </div>
                </div>

                {/* Event description */}
                <div style={{
                  fontSize: '0.9em',
                  color: '#555',
                  lineHeight: '1.4',
                  marginBottom: isActive && event.duration ? '8px' : '0',
                }}>
                  {event.description}
                </div>

                {/* Progress bar for active events */}
                {isActive && event.duration && (
                  <div style={{
                    width: '100%',
                    height: '4px',
                    background: '#ecf0f1',
                    borderRadius: '2px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${progress}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${eventColor}, ${eventColor}dd)`,
                      borderRadius: '2px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* End marker */}
        {timelineEvents.length > 0 && (
          <div style={{
            position: 'relative',
            paddingLeft: '40px',
            marginTop: '20px',
          }}>
            <div style={{
              position: 'absolute',
              left: '-6px',
              top: '8px',
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              background: '#95a5a6',
              border: '2px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }} />
            <div style={{
              fontSize: '0.85em',
              color: '#7f8c8d',
              fontStyle: 'italic',
              marginLeft: '10px',
            }}>
              {orchestratorState.status === 'ready' 
                ? 'Orchestration complete - Ready for next request'
                : 'Orchestration in progress...'}
            </div>
          </div>
        )}
      </div>

      {/* CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}
      </style>
    </div>
  );
};

export default OrchestrationTimeline;